import React, { useRef } from "react";
import { Link } from "react-router-dom";

const Button = ({
  title,
  titleWrap = false,
  type = "button", //button, tab, icon, link
  variant = "contained", // contained, outlined, text
  size = "md", //xs, xs-sm, sm, md, lg, xl
  color = "primary", // primary, warning, success, danger, info, black, white
  icon,
  iconPosition = "before",
  onClick,
  disabled = false,
  active = false,
  className = "",
  to = "", // Add this prop for link type
  href = "", // Add this for external links
  onMouseEnter,
  onMouseLeave,
  fromLeft = false,
  value,
  ddMenu = false,
  ...rest
}) => {
  const buttonRef = useRef(null);

  const typeStyles = {
    button: "rounded text-nowrap",
    tab: "rounded-t border-b-0",
    icon: "rounded-full aspect-square p-0",
    link: "rounded", // Same as button style
  };
  /**
   * Safely converts a value to a number.
   * Returns 0 if the result is NaN or not finite.
   */
  function toSafeNumber(value) {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  }

  const renderContent = () => {
    switch (type) {
      case "icon":
        return (
          <span
            className={`material-symbols-rounded ${
              type === "icon" ? sizeStyles.iconFont[size] : ""
            }`}
          >
            {icon}
          </span>
        );
      case "tab":
      case "button":
      default:
        return (
          <>
            {icon && iconPosition === "before" && (
              <span
                className={`material-symbols-rounded mr-2 ${sizeStyles.iconFont[size]}`}
              >
                {icon}
              </span>
            )}

            <div
              className={`${
                titleWrap ? "text-wrap" : "text-nowrap"
              } flex justify-between gap-4`}
            >
              <div>{title}</div>
              {/* {value && (
                <div
                  className={`${
                    toSafeNumber(value) === 0 ? "bg-red-500" : "bg-blue-500"
                  } text-white text-[10px] leading-none flex justify-center items-center py-0.5 px-1 rounded`}
                >
                  {value}
                </div>
              )} */}
              {value !== null && value !== undefined && (
                <div
                  className={`${
                    toSafeNumber(value) === 0 ? "bg-neutrals" : "bg-black"
                  } text-white text-[10px] leading-none flex justify-center items-center py-0.5 px-1 rounded`}
                >
                  {value}
                </div>
              )}
            </div>
            {icon && iconPosition === "after" && (
              <span
                className={`material-symbols-rounded ml-2 ${sizeStyles.iconFont[size]}`}
              >
                {icon}
              </span>
            )}
          </>
        );
    }
  };

  ////////////////////////////////////////////
  // Function to create ripple effect (Only for contained & outlined)
  const createRipple = (event) => {
    //if (disabled || variant === "text") return; // No ripple for disabled or text variant

    const button = buttonRef.current;
    const ripple = document.createElement("span");

    // Get button dimensions and click position
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    // Define ripple colors separately for contained & outlined variants
    const rippleColors = {
      contained: {
        primary: "bg-primary-50",
        warning: "bg-warning-50",
        success: "bg-success-50",
        danger: "bg-danger-50",
        black: "bg-gray-50",
        white: "bg-gray-50",
        info: "bg-info-50",
      },

      outlined: {
        primary: "bg-primary-300",
        warning: "bg-warning-300",
        success: "bg-success-300",
        danger: "bg-danger-300",
        black: "bg-gray-400",
        white: "bg-gray-300",
        info: "bg-info-300",
      },
      text: {
        primary: "bg-primary-500",
        warning: "bg-warning-500",
        success: "bg-success-500",
        danger: "bg-danger-500",
        black: "bg-gray-500",
        white: "bg-gray-500",
        info: "bg-info-500",
      },
    };

    // Apply Tailwind classes to ripple
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.className = `absolute rounded-full ${
      // rippleColors[variant][color] || "bg-gray-300"
      rippleColors?.[variant]?.[color] ?? "bg-gray-300"
    } opacity-50 animate-ripple`;

    // Append ripple to button
    button.appendChild(ripple);

    // Remove ripple after animation completes
    setTimeout(() => {
      ripple.remove();
    }, 600);
  };

  // Define color styles
  const colors = {
    primary: {
      contained: `${
        type === "tab"
          ? active
            ? "bg-primary-700 text-white border border-primary-700"
            : "bg-primary text-white hover:bg-primary-600 border border-primary"
          : active
          ? "bg-primary-700 text-white border border-primary-700"
          : "bg-primary text-white hover:bg-primary-600 border border-primary"
      }`,

      outlined: `${
        type === "tab"
          ? active
            ? "bg-primary-50 border border-primary text-primary"
            : "border border-primary text-primary hover:bg-primary-50"
          : active
          ? "bg-primary-50 border border-primary text-primary"
          : //: "border border-primary text-primary hover:bg-primary-50"
            "border border-primary text-primary hover:bg-primary-500 hover:text-white"
      }`,
      text: `${
        type === "tab"
          ? active
            ? "border-b-2 border-primary bg-transparent text-primary"
            : "border-b-2 border-transparent text-primary bg-gray-50"
          : active
          ? "bg-primary-50 text-primary"
          : "text-primary hover:bg-primary-50"
      }`,
    },
    warning: {
      contained: `${
        type === "tab"
          ? active
            ? "bg-warning-700 text-white border border-warning-700"
            : "bg-warning text-white hover:bg-warning-600 border border-warning"
          : active
          ? "bg-warning-700 text-white border border-warning-700"
          : "bg-warning text-white hover:bg-warning-600 border border-warning"
      }`,
      outlined: `${
        type === "tab"
          ? active
            ? "bg-warning-50 border border-warning text-warning"
            : "border border-warning text-warning hover:bg-warning-50"
          : active
          ? "bg-warning-50 border border-warning text-warning"
          : "border border-warning text-warning hover:bg-warning-50"
      }`,

      text: `${
        type === "tab"
          ? active
            ? "border-b-2 border-warning bg-transparent text-warning"
            : "border-b-2 border-transparent text-warning bg-gray-50"
          : active
          ? "bg-warning-50 text-warning"
          : "text-warning hover:bg-warning-50"
      }`,
    },
    success: {
      contained: `${
        type === "tab"
          ? active
            ? "bg-success-700 text-white border border-success-700"
            : "bg-success text-white hover:bg-success-600 border border-success"
          : active
          ? "bg-success-700 text-white border border-success-700"
          : "bg-success text-white hover:bg-success-600 border border-success"
      }`,
      outlined: `${
        type === "tab"
          ? active
            ? "bg-success-50 border border-success text-success"
            : "border border-success text-success hover:bg-success-50"
          : active
          ? "bg-success-50 border border-success text-success"
          : "border border-success text-success hover:bg-success-50"
      }`,

      text: `${
        type === "tab"
          ? active
            ? "border-b-2 border-success bg-transparent text-success"
            : "border-b-2 border-transparent text-success bg-gray-50"
          : active
          ? "bg-success-50 text-success"
          : "text-success hover:bg-success-50"
      }`,
    },
    danger: {
      contained: `${
        type === "tab"
          ? active
            ? "bg-danger-700 text-white border border-danger-700"
            : "bg-danger text-white hover:bg-danger-600 border border-danger"
          : active
          ? "bg-danger-700 text-white border border-danger-700"
          : "bg-danger text-white hover:bg-danger-600 border border-danger"
      }`,
      outlined: `${
        type === "tab"
          ? active
            ? "bg-danger-50 border border-danger text-danger"
            : "border border-danger text-danger hover:bg-danger-50"
          : active
          ? "bg-danger-50 border border-danger text-danger"
          : "border border-danger text-danger hover:bg-danger-50"
      }`,

      text: `${
        type === "tab"
          ? active
            ? "border-b-2 border-danger bg-transparent text-danger"
            : "border-b-2 border-transparent text-danger bg-gray-50"
          : active
          ? "bg-danger-50 text-danger"
          : "text-danger hover:bg-danger-50"
      }`,
    },
    info: {
      contained: `${
        type === "tab"
          ? active
            ? "bg-info-700 text-white border border-info-700"
            : "bg-info text-white hover:bg-info-600 border border-info"
          : active
          ? "bg-info-700 text-white border border-info-700"
          : "bg-info text-white hover:bg-info-600 border border-info"
      }`,
      outlined: `${
        type === "tab"
          ? active
            ? "bg-info-50 border border-info text-info"
            : "border border-info text-info hover:bg-info-50"
          : active
          ? "bg-info-50 border border-info text-info"
          : "border border-info text-info hover:bg-info-50"
      }`,

      text: `${
        type === "tab"
          ? active
            ? "border-b-2 border-info bg-transparent text-info"
            : "border-b-2 border-transparent text-info bg-gray-50"
          : active
          ? "bg-info-50 text-info"
          : "text-info hover:bg-info-50"
      }`,
    },

    black: {
      contained: `${
        type === "tab"
          ? active
            ? "bg-gray-700 text-white border border-gray-700"
            : "bg-gray-600 border-gray-600 text-white hover:bg-gray-700 border hover:border-gray-700"
          : active
          ? "bg-gray-700 text-white border border-gray-700"
          : "bg-gray-600 border-gray-600 text-white hover:bg-gray-700 border hover:border-gray-700"
      }`,
      outlined: `${
        type === "tab"
          ? active
            ? "bg-gray-200 bg-opacity-50 border border-gray-200 text-black"
            : "border border-gray-200 text-black hover:bg-gray-200"
          : active
          ? "bg-gray-200 bg-opacity-50 border border-black text-black"
          : "border border-black text-black hover:bg-gray-200"
      }`,

      text: `${
        type === "tab"
          ? active
            ? "border-b-2 border-black bg-transparent text-black"
            : "border-b-2 border-transparent text-black bg-gray-50"
          : active
          ? "bg-gray-100 text-black bg-opacity-50 "
          : "text-black hover:bg-gray-100"
      }`,
    },
    white: {
      contained: `${
        type === "tab"
          ? active
            ? "bg-gray-300 text-black border border-gray-300"
            : "bg-gray-50 border border-gray-50 text-black hover:bg-gray-300 hover:border-gray-300"
          : active
          ? "bg-gray-300 text-black border border-gray-300"
          : "bg-gray-50 border border-gray-50 text-black hover:bg-gray-300 hover:border-gray-300"
      }`,
      outlined: `${
        type === "tab"
          ? active
            ? "bg-gray-500 bg-opacity-50 border border-white text-white"
            : "border border-white text-white hover:bg-gray-500 hover:bg-opacity-50 hover:border-white"
          : active
          ? "bg-gray-500 bg-opacity-50 border border-white text-white"
          : "border border-white text-white hover:bg-gray-500 hover:bg-opacity-50 hover:border-white"
      }`,

      text: `${
        type === "tab"
          ? active
            ? "border-b-2 border-white bg-transparent text-white"
            : "border-b-2 border-transparent text-white bg-transparent"
          : active
          ? "bg-gray-500 bg-opacity-50 text-white"
          : "text-white hover:bg-gray-500 hover:bg-opacity-50"
      }`,
    },
  };

  // Define size styles
  const sizeStyles = {
    button: {
      xs: "text-[10px] px-2 py-0.5",
      "xs-sm": "text-[12px] px-2 py-0.5",
      sm: "text-[12px] px-3 py-1.5", ///this is final
      md: "text-[14px] px-4 py-2", ///this is final
      lg: "text-[16px] px-5 py-2.5",
      xl: "text-[18px] px-7 py-3",
    },
    tab: {
      xs: "text-[10px] leaading-0 px-4 py-2",
      "xs-sm": "text-[11px] leaading-0 px-4 py-2",
      sm: "text-[12px] leaading-0 px-4 py-2",
      md: "text-[14px] px-4 py-2",
      lg: "text-[16px] px-5 py-2.5",
      xl: "text-[18px] px-7 py-3",
    },
    icon: {
      xs: "w-4 h-4",
      "xs-sm": "w-5 h-5",
      sm: "w-7 h-7",
      md: "w-8 h-8",
      lg: "w-10 h-10",
      xl: "w-12 h-12",
    },
    iconFont: {
      xs: "!text-[14px]",
      "xs-sm": "!text-[14px]",
      sm: "!text-[16px]", // Final values
      md: "!text-[20px]", // Final values
      lg: "!text-[22px]",
      xl: "!text-[24px]",
    },
  };

  // Disabled styles
  const disabledStyles = {
    contained:
      "bg-gray-200 text-gray-400 !cursor-not-allowed border border-gray-200",
    outlined: "border border-gray-200 text-gray-400 !cursor-not-allowed",
    text: "text-gray-200 !cursor-not-allowed",
  };

  // const commonProps = {
  //   ref: buttonRef,
  //   className: `relative overflow-hidden transition-all duration-100 ease-in-out flex items-center  select-none outline-none
  //   ${fromLeft ? "justify-start" : "justify-center"}
  //     ${typeStyles[type]}
  //     ${disabled ? disabledStyles[variant] : colors[color]?.[variant]}
  //     ${
  //       type === "icon"
  //         ? sizeStyles.icon[size]
  //         : sizeStyles[type]?.[size] || sizeStyles.button[size]
  //     }
  //     ${className}`,
  // };

  const commonProps = {
    ref: buttonRef,
    className: `relative overflow-hidden transition-colors duration-0 ease-in-out flex items-center select-none outline-none
    ${fromLeft ? "justify-start" : "justify-center"}
      ${typeStyles[type]}
      ${disabled ? disabledStyles[variant] : colors[color]?.[variant]} 
      ${
        type === "icon"
          ? sizeStyles.icon[size]
          : sizeStyles[type]?.[size] || sizeStyles.button[size]
      }
      [transition:background-color_600ms,border-color_600ms]
      ${className}`,
  };

  // Separate ripple handler
  const handleRipple = (e) => {
    if (variant !== "") createRipple(e);
    if (onClick) onClick(e);
  };

  if (type === "link") {
    if (href) {
      return (
        <a
          {...commonProps}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleRipple}
        >
          {renderContent()}
        </a>
      );
    }
    return (
      <Link {...commonProps} to={to} onClick={handleRipple}>
        {renderContent()}
      </Link>
    );
  }

  return (
    <button
      {...commonProps}
      onClick={handleRipple}
      disabled={disabled}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...rest}
    >
      {renderContent()}
      {ddMenu && (
        <span class="material-symbols-outlined !text-[10px]">
          keyboard_arrow_down
        </span>
      )}
    </button>
  );
};

export default Button;
